import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Loader2, Trash2, ShieldAlert, Building2, KeyRound, FileSignature } from "lucide-react";
import { toast } from "sonner";
import { useSuccessionStageState, CREDENTIAL_MAP_CATEGORIES, credentialSecretGuard } from "@/lib/succession/stages";

type InstitutionRow = {
  id: string;
  institution_name: string;
  institution_type: string;
  path: "signer_in_waiting" | "letter_package";
  requirements_note: string | null;
  status: "pending" | "ready";
};

type CredentialRow = {
  id: string;
  category: string;
  label: string;
  location_reference: string | null;
  handling_rule: string | null;
};

const INSTITUTION_TYPE_LABELS: Record<string, string> = {
  assoc_bank: "Association bank",
  llc_bank: "LLC bank",
  brokerage: "Brokerage",
  exchange: "Exchange",
  insurance: "Insurance",
  other: "Other",
};

const DOC_TITLES: Record<string, string> = {
  continuity_resolution: "Private Stewardship Continuity Resolution",
  standing_successor_resolution: "Standing Successor Resolution",
  incapacity_protocol: "Incapacity Protocol",
  founders_letter: "The Founder's Letter",
  successors_manual: "The Successor's Manual",
  successor_manager_acceptance: "Successor Manager Acceptance",
  readiness_transition_resolution: "Readiness Transition Resolution",
};

export default function SuccessionExecution() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const s = useSuccessionStageState(user?.id);

  const [institutions, setInstitutions] = useState<InstitutionRow[]>([]);
  const [credentials, setCredentials] = useState<CredentialRow[]>([]);
  const [savingInst, setSavingInst] = useState(false);
  const [savingCred, setSavingCred] = useState(false);
  const [instDraft, setInstDraft] = useState({
    institution_name: "", institution_type: "assoc_bank",
    path: "signer_in_waiting" as "signer_in_waiting" | "letter_package",
    requirements_note: "",
  });
  const [credDraft, setCredDraft] = useState({
    category: CREDENTIAL_MAP_CATEGORIES[0] as string, label: "",
    location_reference: "", handling_rule: "",
  });
  const [confirmA, setConfirmA] = useState(false);
  const [confirmB, setConfirmB] = useState(false);
  const [markingComplete, setMarkingComplete] = useState(false);

  useEffect(() => {
    if (!s.unaId || !s.profileId) return;
    (async () => {
      const [{ data: inst }, { data: creds }] = await Promise.all([
        supabase.from("institution_access_register")
          .select("id, institution_name, institution_type, path, requirements_note, status")
          .eq("profile_id", s.profileId).order("created_at", { ascending: true }),
        supabase.from("credential_map_items")
          .select("id, category, label, location_reference, handling_rule")
          .eq("association_id", s.unaId).order("category", { ascending: true }),
      ]);
      setInstitutions((inst as any) || []);
      setCredentials((creds as any) || []);
    })();
  }, [s.unaId, s.profileId]);

  const docsSignedFiled = s.docRows.filter((r) => r.status === "signed_filed").length;
  const applicableDrafted = s.applicableDocTypes.length;
  const allDocsFiled = s.applicableDocTypes.length > 0 &&
    s.applicableDocTypes.every((t) => s.docRows.find((r) => r.doc_type === t)?.status === "signed_filed");

  const canComplete = allDocsFiled && confirmA && confirmB;

  async function addInstitution() {
    if (!s.unaId || !s.profileId) return;
    if (!instDraft.institution_name.trim()) {
      toast.error("Please enter the institution's name.");
      return;
    }
    setSavingInst(true);
    const { data, error } = await supabase.from("institution_access_register").insert({
      association_id: s.unaId, profile_id: s.profileId,
      institution_name: instDraft.institution_name.trim(),
      institution_type: instDraft.institution_type,
      path: instDraft.path,
      requirements_note: instDraft.requirements_note.trim() || null,
      status: "pending",
      created_by: user?.id,
    }).select("id, institution_name, institution_type, path, requirements_note, status").single();
    setSavingInst(false);
    if (error) { toast.error("Could not save. Please try again."); return; }
    setInstitutions((prev) => [...prev, data as any]);
    setInstDraft({ institution_name: "", institution_type: "assoc_bank", path: "signer_in_waiting", requirements_note: "" });
  }

  async function toggleReady(row: InstitutionRow, ready: boolean) {
    const next = ready ? "ready" : "pending";
    setInstitutions((prev) => prev.map((r) => r.id === row.id ? { ...r, status: next } : r));
    const { error } = await supabase.from("institution_access_register").update({ status: next }).eq("id", row.id);
    if (error) toast.error("Could not update.");
  }

  async function deleteInstitution(id: string) {
    setInstitutions((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("institution_access_register").delete().eq("id", id);
  }

  async function addCredential() {
    if (!s.unaId) return;
    if (!credDraft.label.trim()) {
      toast.error("Please enter a label.");
      return;
    }
    const g1 = credentialSecretGuard(credDraft.location_reference);
    const g2 = credentialSecretGuard(credDraft.handling_rule);
    if (g1 || g2) { toast.error(g1 || g2!); return; }
    setSavingCred(true);
    const { data, error } = await supabase.from("credential_map_items").insert({
      association_id: s.unaId,
      category: credDraft.category,
      label: credDraft.label.trim(),
      location_reference: credDraft.location_reference.trim() || null,
      handling_rule: credDraft.handling_rule.trim() || null,
      created_by: user?.id,
    }).select("id, category, label, location_reference, handling_rule").single();
    setSavingCred(false);
    if (error) { toast.error("Could not save."); return; }
    setCredentials((prev) => [...prev, data as any]);
    setCredDraft({ category: credDraft.category, label: "", location_reference: "", handling_rule: "" });
  }

  async function deleteCredential(id: string) {
    setCredentials((prev) => prev.filter((r) => r.id !== id));
    await supabase.from("credential_map_items").delete().eq("id", id);
  }

  async function markStageComplete() {
    if (!s.profileId || !canComplete) return;
    setMarkingComplete(true);
    const nextDecisions = { ...(s.decisions || {}), execution_complete: true };
    const { error } = await supabase.from("succession_profiles")
      .update({ decisions: nextDecisions })
      .eq("id", s.profileId);
    setMarkingComplete(false);
    if (error) { toast.error("Could not save."); return; }
    toast.success("Execution stage complete.");
    navigate("/succession/rhythm");
  }

  if (s.loading) {
    return (
      <MainLayout>
        <div className="max-w-5xl mx-auto py-12 px-4">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!s.unaId) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6"><p>You must be an Association administrator to use this page.</p></Card>
        </div>
      </MainLayout>
    );
  }

  if (s.isPaused) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6">
            <p className="text-amber-900">Your succession record is paused for review. Our team will reach out.</p>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (!s.stage5Unlocked) {
    return (
      <MainLayout>
        <div className="max-w-3xl mx-auto py-12 px-4">
          <Card className="p-6 space-y-3">
            <h1 className="text-2xl font-serif">Execution</h1>
            <p className="text-muted-foreground">Finish your documents first. Every paper that applies to your family needs a draft, and the Continuity Resolution needs to be signed and filed.</p>
            <Button onClick={() => navigate("/succession/documents")}>Go to your documents</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  const groupedCreds = CREDENTIAL_MAP_CATEGORIES.map((cat) => ({
    category: cat,
    items: credentials.filter((c) => c.category === cat),
  }));

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto py-8 px-4 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/succession/overview")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Succession
          </Button>
          {s.stage5Done && <Badge className="bg-emerald-600 hover:bg-emerald-600">Stage complete</Badge>}
        </div>

        <div>
          <h1 className="text-3xl font-serif">Execution</h1>
          <p className="text-muted-foreground mt-2">This is the stage where things get signed, filed, and set up.</p>
        </div>

        {/* Section 1: Sign and file */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <FileSignature className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-serif">Sign and file</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            {docsSignedFiled} of {applicableDrafted} signed and filed.
          </p>
          <div className="space-y-2">
            {s.applicableDocTypes.map((t) => {
              const row = s.docRows.find((r) => r.doc_type === t);
              const label = DOC_TITLES[t] || t;
              const status =
                row?.status === "signed_filed" ? "Signed and filed" :
                row ? "Drafted" : "Not drafted";
              const variant =
                row?.status === "signed_filed" ? "bg-emerald-600 hover:bg-emerald-600" :
                row ? "bg-amber-500 hover:bg-amber-500" : "";
              return (
                <div key={t} className="flex items-center justify-between border rounded-md p-3">
                  <div>
                    <p className="font-medium text-sm">{label}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={variant} variant={row ? undefined : "outline"}>{status}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => navigate("/succession/documents")}>Open</Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Section 2: The banks */}
        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-serif">The banks</h2>
          </div>
          <div className="text-sm space-y-3 text-muted-foreground leading-relaxed">
            <p>Banks honor their own paperwork, not what we write inside the Association. So we set up two paths ahead of time.</p>
            <p><strong className="text-foreground">Path one:</strong> where the bank allows it, your successor is added now as a signer in waiting under your banking decision, so the trigger day changes nothing at the counter.</p>
            <p><strong className="text-foreground">Path two:</strong> everywhere else, you learn today what that bank will ask for on the day, and you keep it listed here, so your successor walks in ready instead of finding out while grieving.</p>
            <p>Banks often ask for a certified death certificate, the successor's government photo ID, your Continuity Resolution and Standing Successor Resolution, and their own forms. Call each bank and ask. Write down what they say.</p>
          </div>

          <div className="border rounded-md p-4 space-y-3 bg-muted/30">
            <p className="text-sm font-medium">Add an institution</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="inst-name">Institution name</Label>
                <Input id="inst-name" value={instDraft.institution_name}
                  onChange={(e) => setInstDraft((d) => ({ ...d, institution_name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inst-type">Type</Label>
                <Select value={instDraft.institution_type}
                  onValueChange={(v) => setInstDraft((d) => ({ ...d, institution_type: v }))}>
                  <SelectTrigger id="inst-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(INSTITUTION_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Path</Label>
              <RadioGroup value={instDraft.path} onValueChange={(v: any) => setInstDraft((d) => ({ ...d, path: v }))}
                className="grid md:grid-cols-2 gap-2 mt-1">
                <label className="flex items-start gap-2 border rounded p-2 cursor-pointer">
                  <RadioGroupItem value="signer_in_waiting" className="mt-1" />
                  <div>
                    <div className="text-sm font-medium">Signer in waiting</div>
                    <div className="text-xs text-muted-foreground">Add now under the banking resolution.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 border rounded p-2 cursor-pointer">
                  <RadioGroupItem value="letter_package" className="mt-1" />
                  <div>
                    <div className="text-sm font-medium">Letter package</div>
                    <div className="text-xs text-muted-foreground">Prepare exactly what this institution will require on the day.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>
            <div className="space-y-1">
              <Label htmlFor="inst-note">Requirements note</Label>
              <Textarea id="inst-note" rows={2}
                placeholder="What this institution says it will need"
                value={instDraft.requirements_note}
                onChange={(e) => setInstDraft((d) => ({ ...d, requirements_note: e.target.value }))} />
            </div>
            <Button size="sm" onClick={addInstitution} disabled={savingInst}>
              {savingInst && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add institution
            </Button>
          </div>

          {institutions.length > 0 && (
            <div className="space-y-2">
              {institutions.map((row) => (
                <div key={row.id} className="border rounded-md p-3 space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium">{row.institution_name}</p>
                        <Badge variant="outline">{INSTITUTION_TYPE_LABELS[row.institution_type] || row.institution_type}</Badge>
                        <Badge variant="outline">{row.path === "signer_in_waiting" ? "Signer in waiting" : "Letter package"}</Badge>
                      </div>
                      {row.requirements_note && (
                        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{row.requirements_note}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch checked={row.status === "ready"} onCheckedChange={(v) => toggleReady(row, v)} />
                        <span className="text-xs text-muted-foreground">Ready</span>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => deleteInstitution(row.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border border-amber-300 bg-amber-50 rounded-md p-4 flex items-start gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-800 mt-0.5" />
            <p className="text-sm text-amber-900">
              If any bank ever freezes an account or asks for a compliance interview, stop. Do not answer on your own. Reach the team through <button className="underline" onClick={() => navigate("/support")}>Ask a person</button> in Support, and talk to a licensed professional first.
            </p>
          </div>
        </Card>

        {/* Section 3: Credential map */}
        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-serif">The key map</h2>
          </div>
          <div className="text-sm text-muted-foreground leading-relaxed space-y-3">
            <p>This is a map, never a safe. It lists what exists and where the way in lives. It never holds the secrets themselves.</p>
            <p>No passwords, no PINs, no seed phrases (the words that open a crypto wallet), no account numbers, not here and not anywhere in this portal. Seed phrases stay on paper, kept outside this portal, with only the place they live written down.</p>
          </div>

          <div className="border rounded-md p-4 space-y-3 bg-muted/30">
            <p className="text-sm font-medium">Add an entry</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="cred-cat">Category</Label>
                <Select value={credDraft.category}
                  onValueChange={(v) => setCredDraft((d) => ({ ...d, category: v }))}>
                  <SelectTrigger id="cred-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CREDENTIAL_MAP_CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="cred-label">Label</Label>
                <Input id="cred-label" value={credDraft.label}
                  onChange={(e) => setCredDraft((d) => ({ ...d, label: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cred-loc">Location reference</Label>
              <Input id="cred-loc" placeholder="Where access lives, in general terms"
                value={credDraft.location_reference}
                onChange={(e) => setCredDraft((d) => ({ ...d, location_reference: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cred-rule">Handling rule</Label>
              <Textarea id="cred-rule" rows={2}
                placeholder="How a successor under an activated trigger reaches this, and what the holder will require"
                value={credDraft.handling_rule}
                onChange={(e) => setCredDraft((d) => ({ ...d, handling_rule: e.target.value }))} />
            </div>
            <Button size="sm" onClick={addCredential} disabled={savingCred}>
              {savingCred && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Add entry
            </Button>
          </div>

          <div className="space-y-4">
            {groupedCreds.filter((g) => g.items.length > 0).map((g) => (
              <div key={g.category}>
                <p className="text-sm font-medium mb-2">{g.category}</p>
                <div className="space-y-2">
                  {g.items.map((row) => (
                    <div key={row.id} className="border rounded p-3 flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <p className="font-medium text-sm">{row.label}</p>
                        {row.location_reference && (
                          <p className="text-xs text-muted-foreground mt-1"><strong>Location:</strong> {row.location_reference}</p>
                        )}
                        {row.handling_rule && (
                          <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap"><strong>Handling:</strong> {row.handling_rule}</p>
                        )}
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => deleteCredential(row.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Completion checklist */}
        <Card className="p-6 space-y-4">
          <h2 className="text-xl font-serif">Ready to complete this stage?</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox checked={allDocsFiled} disabled className="mt-1" />
              <div>
                <p className="text-sm">All applicable documents signed and filed</p>
                <p className="text-xs text-muted-foreground">{docsSignedFiled} of {applicableDrafted}</p>
              </div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmA} onCheckedChange={(v) => setConfirmA(!!v)} className="mt-1" />
              <p className="text-sm">Every bank the Association leans on is in the list with its path picked.</p>
            </label>
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox checked={confirmB} onCheckedChange={(v) => setConfirmB(!!v)} className="mt-1" />
              <p className="text-sm">The key map covers everything the Association leans on.</p>
            </label>
          </div>
          <Button onClick={markStageComplete} disabled={!canComplete || markingComplete}>
            {markingComplete && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Mark Execution complete
          </Button>
        </Card>
      </div>
    </MainLayout>
  );
}
